import React, { useEffect, useMemo, useState } from 'react';
import { FiCreditCard, FiChevronDown, FiChevronRight, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { costReportService } from '../../services/apiService';
import { getISTDayRanges } from '../../utils/dateUtils';
import { useT } from '../../utils/i18n';

const DEFAULT_RANGE = 'thisMonth';

function formatUSD(value) {
  const n = value || 0;
  // Small amounts keep their cents' fractions so a $0.004 call isn't "$0.00".
  const digits = n > 0 && n < 1 ? 3 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function formatPercent(value) {
  if (!value) return '0%';
  return value < 0.1 ? '<0.1%' : `${value.toFixed(1)}%`;
}

function formatDay(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

// One share of the total: a thin bar on a gray track, the number beside it.
const ShareBar = ({ percent }) => (
  <div className="h-1.5 w-full rounded-full bg-gray-100" aria-hidden="true">
    <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${Math.min(100, Math.max(percent, 0.5))}%` }} />
  </div>
);

const StatTile = ({ label, value, hint }) => (
  <div className="card !p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums truncate">{value}</p>
    {hint && <p className="mt-0.5 text-xs text-gray-500 truncate">{hint}</p>}
  </div>
);

export const UsageBilling = () => {
  const t = useT();
  const ranges = useMemo(() => getISTDayRanges(), []);
  const [rangeKey, setRangeKey] = useState(DEFAULT_RANGE);
  const preset = ranges.find((r) => r.key === DEFAULT_RANGE);
  const [from, setFrom] = useState(preset.from);
  const [to, setTo] = useState(preset.to);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openUser, setOpenUser] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const activityLabel = (a) => {
    const key = `usage.activity.${a.key}`;
    const text = t(key);
    return text === key ? a.label : text;
  };
  const kindLabel = (kind) => {
    const key = `usage.kind.${kind}`;
    const text = t(key);
    return text === key ? kind : text;
  };

  const pickRange = (key) => {
    setRangeKey(key);
    const r = ranges.find((x) => x.key === key);
    if (r) { setFrom(r.from); setTo(r.to); }
  };

  const rangeValid = from && to && from <= to;

  useEffect(() => {
    if (!rangeValid) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    costReportService.get({ from, to })
      .then((data) => { if (!cancelled) setReport(data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.message || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, rangeValid, reloadKey]);

  const topActivity = report?.byActivity?.[0];
  const topUser = report?.byUser?.[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiCreditCard size={20} className="text-primary-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{t('settings.usageBilling')}</h1>
          <p className="text-sm text-gray-500">{t('usage.subtitle')}</p>
        </div>
      </div>

      {/* Date range — presets, or any custom range */}
      <div className="card !p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {ranges.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => pickRange(r.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                rangeKey === r.key
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t(`usage.range.${r.key}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-600">
            <span className="block mb-1">{t('usage.from')}</span>
            <input
              type="date"
              className="input !py-1.5 !w-auto"
              value={from}
              max={to || undefined}
              onChange={(e) => { setFrom(e.target.value); setRangeKey('custom'); }}
            />
          </label>
          <label className="text-sm text-gray-600">
            <span className="block mb-1">{t('usage.to')}</span>
            <input
              type="date"
              className="input !py-1.5 !w-auto"
              value={to}
              min={from || undefined}
              onChange={(e) => { setTo(e.target.value); setRangeKey('custom'); }}
            />
          </label>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading || !rangeValid}
            className="btn btn-ghost btn-sm !py-1.5"
            title={t('usage.refresh')}
          >
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('usage.refresh')}
          </button>
          {rangeValid && (
            <p className="text-sm text-gray-500 pb-1.5">
              {formatDay(from)} – {formatDay(to)}
            </p>
          )}
        </div>
        {!rangeValid && <p className="text-sm text-danger-600">{t('usage.badRange')}</p>}
      </div>

      {error && (
        <div className="card !p-4 border-danger-100 bg-danger-50 text-sm text-danger-700">{error}</div>
      )}

      {report && (
        <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label={t('usage.totalSpend')} value={formatUSD(report.totalUSD)} />
            <StatTile label={t('usage.paidCalls')} value={report.calls.toLocaleString('en-IN')} />
            <StatTile
              label={t('usage.topActivity')}
              value={topActivity ? activityLabel(topActivity) : '—'}
              hint={topActivity ? `${formatUSD(topActivity.costUSD)} · ${formatPercent(topActivity.percent)}` : ''}
            />
            <StatTile
              label={t('usage.topUser')}
              value={topUser ? topUser.name : '—'}
              hint={topUser ? `${formatUSD(topUser.costUSD)} · ${formatPercent(topUser.percent)}` : ''}
            />
          </div>

          {report.totalUSD === 0 ? (
            <div className="card flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
              <FiCreditCard size={32} className="text-gray-300" />
              <p className="text-sm">{t('usage.noSpend')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
              {/* By activity */}
              <section className="card !p-0 overflow-hidden">
                <h2 className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">{t('usage.byActivity')}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase tracking-wide">
                      <tr className="text-left">
                        <th className="px-4 py-2 font-medium">{t('usage.activity')}</th>
                        <th className="px-4 py-2 font-medium text-right">{t('usage.cost')}</th>
                        <th className="px-4 py-2 font-medium w-1/3">{t('usage.share')}</th>
                        <th className="hidden sm:table-cell px-4 py-2 font-medium text-right">{t('usage.calls')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report.byActivity.map((a) => (
                        <tr key={a.key} className="align-top">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{activityLabel(a)}</p>
                            <p className="text-xs text-gray-500">
                              {a.byKind.map((k) => `${kindLabel(k.kind)} ${formatUSD(k.costUSD)}`).join(' · ')}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">{formatUSD(a.costUSD)}</td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-700 tabular-nums mb-1">{formatPercent(a.percent)}</p>
                            <ShareBar percent={a.percent} />
                          </td>
                          <td className="hidden sm:table-cell px-4 py-3 text-right text-gray-600 tabular-nums">{a.calls.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* By user — click a row for that person's split by activity */}
              <section className="card !p-0 overflow-hidden">
                <h2 className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">{t('usage.byUser')}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase tracking-wide">
                      <tr className="text-left">
                        <th className="px-4 py-2 font-medium">{t('usage.user')}</th>
                        <th className="px-4 py-2 font-medium text-right">{t('usage.cost')}</th>
                        <th className="px-4 py-2 font-medium w-1/3">{t('usage.share')}</th>
                        <th className="hidden sm:table-cell px-4 py-2 font-medium text-right">{t('usage.calls')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report.byUser.map((u) => {
                        const open = openUser === u.userId;
                        return (
                          <React.Fragment key={u.userId}>
                            <tr
                              className="align-top cursor-pointer hover:bg-gray-50"
                              onClick={() => setOpenUser(open ? null : u.userId)}
                              aria-expanded={open}
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-start gap-1.5">
                                  {open
                                    ? <FiChevronDown size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                                    : <FiChevronRight size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />}
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{u.name}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {[u.role && t(`usage.role.${u.role}`), u.email].filter(Boolean).join(' · ')}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">{formatUSD(u.costUSD)}</td>
                              <td className="px-4 py-3">
                                <p className="text-xs text-gray-700 tabular-nums mb-1">{formatPercent(u.percent)}</p>
                                <ShareBar percent={u.percent} />
                              </td>
                              <td className="hidden sm:table-cell px-4 py-3 text-right text-gray-600 tabular-nums">{u.calls.toLocaleString('en-IN')}</td>
                            </tr>
                            {open && (
                              <tr className="bg-gray-50">
                                <td colSpan={4} className="px-4 pb-3 pt-1">
                                  <ul className="space-y-1.5 pl-5">
                                    {u.byActivity.map((a) => (
                                      <li key={a.key} className="flex items-center gap-3 text-xs">
                                        <span className="w-40 flex-shrink-0 text-gray-700 truncate">{activityLabel(a)}</span>
                                        <span className="w-20 flex-shrink-0 text-right font-medium text-gray-900 tabular-nums">{formatUSD(a.costUSD)}</span>
                                        <span className="w-12 flex-shrink-0 text-right text-gray-500 tabular-nums">{formatPercent(a.percent)}</span>
                                        <span className="flex-1 min-w-[60px]"><ShareBar percent={a.percent} /></span>
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}

          <p className="flex items-start gap-2 text-xs text-gray-500">
            <FiInfo size={14} className="mt-0.5 flex-shrink-0" />
            <span>{t('usage.note', { rate: `$${report.elevenLabsUsdPer1kCredits.toFixed(2)}` })}</span>
          </p>
        </div>
      )}

      {!report && loading && (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-primary-500" />
        </div>
      )}
    </div>
  );
};
